/**
 * Redirect Utilities
 *
 * Handles redirect URL encoding, decoding, and validation for post-login redirects.
 * Implements security measures to prevent open redirect vulnerabilities.
 */

export const REDIRECT_QUERY_PARAM = "redir";

/**
 * Validates if a redirect path is safe to use
 * Prevents open redirect vulnerabilities by:
 * - Only allowing relative paths
 * - Blocking protocol-relative URLs
 * - Whitelisting known safe route prefixes
 * - Blacklisting login routes to prevent loops
 */
export const isValidRedirectPath = (path: string): boolean => {
	// Must be a string
	if (typeof path !== "string" || !path) return false;

	// Must start with / (relative path only)
	if (!path.startsWith("/")) return false;

	// Must not be a protocol-relative URL
	if (path.startsWith("//")) return false;

	// Blacklist: Never redirect to login pages (prevents loops)
	if (path.startsWith("/login")) return false;

	// Whitelist: Only allow specific route patterns
	const allowedPrefixes = [
		"/ops",
		"/menus",
		"/reports",
		"/customers",
		"/settings",
		"/wallets",
		"/offers",
		"/inventories",
		"/printer-logs",
		"/order-logs",
		"/accounting-journals",
		"/integrations",
	];

	return allowedPrefixes.some((prefix) => path.startsWith(prefix));
};

/**
 * Encodes a path for use in redirect query parameter
 */
export const encodeRedirectPath = (path: string): string => {
	return encodeURIComponent(path);
};

/**
 * Decodes and validates a redirect query parameter
 * Returns null if invalid
 */
export const decodeRedirectPath = (
	encoded: string | string[] | undefined,
): string | null => {
	if (!encoded || Array.isArray(encoded)) return null;

	try {
		const decoded = decodeURIComponent(encoded);
		return isValidRedirectPath(decoded) ? decoded : null;
	} catch {
		return null;
	}
};

/**
 * Gets the default post-login destination based on mode
 */
export const getDefaultPostLoginDestination = ({
	isOfflineMode,
}: {
	isOfflineMode: boolean;
}): string => {
	return isOfflineMode ? "/offline/ops" : "/ops";
};

/**
 * Builds login URL with redirect parameter
 */
export const buildLoginUrl = ({
	venueId,
	redirectTo,
}: {
	venueId: string;
	redirectTo?: string;
}): string => {
	const baseUrl = venueId ? `/login/${venueId}` : "/login";

	if (!redirectTo || !isValidRedirectPath(redirectTo)) {
		return baseUrl;
	}

	return `${baseUrl}?${REDIRECT_QUERY_PARAM}=${encodeRedirectPath(redirectTo)}`;
};
